import React, { useRef, useState } from "react";
import type { ImpersonationState } from "./types";
import { initializePendingFromCurrent } from "./selectionState";

interface UseSelectorPopoverOptions {
  pending: ImpersonationState["pending"];
  setImpersonationState: React.Dispatch<
    React.SetStateAction<ImpersonationState>
  >;
}

interface UseSelectorPopoverResult {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  listRef: React.RefObject<HTMLUListElement | null>;
  closeSelector: () => void;
  handleOpen: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleSummaryKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleOptionKeyDown: (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    optionsCount: number,
  ) => void;
}

export function useSelectorPopover({
  pending,
  setImpersonationState,
}: UseSelectorPopoverOptions): UseSelectorPopoverResult {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const ensurePendingInitialized = () => {
    if (pending) {
      return;
    }

    setImpersonationState(initializePendingFromCurrent);
  };

  const closeSelector = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    ensurePendingInitialized();
    setAnchorEl(event.currentTarget);
  };

  const handleSummaryKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    ensurePendingInitialized();
    setAnchorEl(event.currentTarget);
  };

  const focusOption = (index: number) => {
    const options =
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    if (!options || options.length === 0) {
      return;
    }

    options[Math.max(0, Math.min(index, options.length - 1))]?.focus();
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(0);
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    optionsCount: number,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      index === 0 ? searchInputRef.current?.focus() : focusOption(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(optionsCount - 1);
    }
  };

  return {
    anchorEl,
    isOpen,
    searchInputRef,
    listRef,
    closeSelector,
    handleOpen,
    handleSummaryKeyDown,
    handleSearchKeyDown,
    handleOptionKeyDown,
  };
}